import React from 'react';
import { Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { PricingStructure } from '../../types';

interface PricingDisplayProps {
  pricing: PricingStructure;
  propertyName?: string;
  cleaningFee?: number;
}

const PricingDisplay: React.FC<PricingDisplayProps> = ({ pricing, cleaningFee }) => {
  const monthNames = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sc = pricing.seasonalConfig as any;
  const isOldFormat = Array.isArray(sc?.seasons);
  const isNewFormat = !isOldFormat && (sc?.highSeasonRates || sc?.lowSeasonRates);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fmtRange = (s: any) =>
    `${s.startDay}. ${monthNames[s.startMonth - 1]} – ${s.endDay}. ${monthNames[s.endMonth - 1]}`;

  const footer = (
    <div className="space-y-2">
      {cleaningFee != null && cleaningFee > 0 && (
        <div className="flex justify-between items-center text-sm border rounded-lg px-4 py-2.5">
          <span className="text-muted-foreground">Reinigungspauschale</span>
          <span className="font-semibold">CHF {cleaningFee}</span>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Der Preis gilt pro Nacht inklusive aller Nebenkosten. Mindestaufenthalt: 3 Nächte.
      </p>
    </div>
  );

  if (isOldFormat) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const highSeasons: any[] = sc.seasons.filter((s: any) => s.type === 'high');
    const lowSeasons: any[] = [
      ...sc.seasons.filter((s: any) => s.type === 'low'),
      ...(sc.defaultSeason?.type === 'low' ? [sc.defaultSeason] : []),
    ];
    const highWeekRate = highSeasons[0]?.rates?.weekRate ?? 0;
    const lowWeekRate = lowSeasons[0]?.rates?.weekRate ?? 0;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {highSeasons.length > 0 && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="pt-5 space-y-3">
                <span className="font-semibold text-orange-800">Hochsaison</span>
                <div className="text-3xl font-bold text-orange-700">
                  CHF {highWeekRate}
                  <span className="text-base font-normal text-orange-600 ml-1">/ Nacht</span>
                </div>
                <div className="space-y-1">
                  {highSeasons.map((s: any) => (
                    <div key={s.id} className="flex items-center gap-2 text-sm text-orange-700">
                      <Calendar className="h-3.5 w-3.5 shrink-0" />
                      {fmtRange(s)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {lowSeasons.length > 0 && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-5 space-y-3">
                <span className="font-semibold text-blue-800">Nebensaison</span>
                <div className="text-3xl font-bold text-blue-700">
                  CHF {lowWeekRate}
                  <span className="text-base font-normal text-blue-600 ml-1">/ Nacht</span>
                </div>
                <div className="space-y-1">
                  {lowSeasons.map((s: any) => (
                    <div key={s.id} className="flex items-center gap-2 text-sm text-blue-700">
                      <Calendar className="h-3.5 w-3.5 shrink-0" />
                      {fmtRange(s)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        {footer}
      </div>
    );
  }

  if (isNewFormat) {
    const highWeekRate = sc.highSeasonRates?.weekRate ?? 0;
    const lowWeekRate = sc.lowSeasonRates?.weekRate ?? 0;
    const highRanges: any[] = sc.highSeasonRanges ?? [];
    const lowRanges: any[] = sc.lowSeasonRanges ?? [];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {highWeekRate > 0 && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="pt-5 space-y-3">
                <span className="font-semibold text-orange-800">Hochsaison</span>
                <div className="text-3xl font-bold text-orange-700">
                  CHF {highWeekRate}
                  <span className="text-base font-normal text-orange-600 ml-1">/ Nacht</span>
                </div>
                <div className="space-y-1">
                  {highRanges.map((s: any) => (
                    <div key={s.id} className="flex items-center gap-2 text-sm text-orange-700">
                      <Calendar className="h-3.5 w-3.5 shrink-0" />
                      {fmtRange(s)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {lowWeekRate > 0 && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-5 space-y-3">
                <span className="font-semibold text-blue-800">Nebensaison</span>
                <div className="text-3xl font-bold text-blue-700">
                  CHF {lowWeekRate}
                  <span className="text-base font-normal text-blue-600 ml-1">/ Nacht</span>
                </div>
                <div className="space-y-1">
                  {lowRanges.map((s: any) => (
                    <div key={s.id} className="flex items-center gap-2 text-sm text-blue-700">
                      <Calendar className="h-3.5 w-3.5 shrink-0" />
                      {fmtRange(s)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        {footer}
      </div>
    );
  }

  // Legacy fallback
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-5">
          <div className="text-3xl font-bold">
            CHF {pricing.combinedRate || pricing.weekdayRate}
            <span className="text-base font-normal text-muted-foreground ml-1">/ Nacht</span>
          </div>
        </CardContent>
      </Card>
      {footer}
    </div>
  );
};

export default PricingDisplay;
